import { EntitySelectOptions } from "../../Customization/CustomFields/CustomFieldUtils";
import { labNoteBooksConstants } from "../../ELN/types/LabNotebook";
import { labNotebookEntriesConstants } from "../../ELN/types/LabNotebookEntry";
import { labNotebookExperimentsConstants } from "../../ELN/types/LabNotebookExperiment";
import { datasetsConstants } from "../Datasets";
import {
  PropertyTranslator,
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  INamedEntityParametersTranslator,
  INamedEntityLabels,
  IPermissionedEntityLabels,
} from "../GenericTranslator";
import {
  IModelWithIntId,
  INamedEntity,
  IPermissionedEntity,
  IEntityMinimalModel,
  IRelationModel,
  GenericEntityOrderBy,
  IGenericRequestParameters,
  IPaginationParameters,
  EntityConstants,
  INamedEntityParameters,
  CustomFieldSearchOperator,
  NamedEntityOrderBy,
} from "../GenericTypes";
import { inventoriesConstants, inventoryItemsConstants } from "../Inventories";
import { personsConstants } from "../Person";
import { projectsConstants } from "../Projects";
import { samplesConstants } from "../Samples";
import { sharedContentConstants } from "../SharedContent";
import { methodsConstants } from "../Method";
import { attachmentsConstants } from "../Attachments";

export interface CustomFieldBase extends IModelWithIntId, INamedEntity, IPermissionedEntity {
  description: string;
  placeholder: string;
  dataType: CustomFieldDataType;
  customTypeConstraint?: IEntityMinimalModel<CustomFieldBase["id"]>[] | null;
  required?: boolean;
  readOnly?: boolean;
  validationRegexp?: string;
  validationMessage?: string;
  showAsTextArea?: boolean;
  predefinedOptions?: any[];
  predefinedOptionsFromValues?: boolean;
  defaultValues?: any;
  templateVersion?: string;
  integrations?: string[];
  useUUID?: boolean;
}

export const CustomFieldBaseFieldLabels: PropertyTranslator<CustomFieldBase> & {
  isMultiValue: string;
  isTimeRange: string;
  isFloat: string;
  isDateTime: string;
  isDateTimeRange: string;
  entitySelectOption: string;
} = {
  id: "Custom field ID",
  ...INamedEntityLabels,
  ...IPermissionedEntityLabels,
  description: "Description",
  placeholder: "Placeholder",
  dataType: "Data type",
  customTypeConstraint: "Custom type constraints",
  required: "Required",
  readOnly: "Read-only",
  validationRegexp: "Validation regular expression",
  validationMessage: "Validation message",
  showAsTextArea: "Multiline text",
  predefinedOptions: "Predefined options",
  predefinedOptionsFromValues: "Dynamic options",
  defaultValues: "Default value",
  isMultiValue: "Multi selectable",
  isTimeRange: "Time range",
  isFloat: "Floating precision",
  isDateTime: "Date and time",
  isDateTimeRange: "Date and time range",
  entitySelectOption: "Entity reference type",
  templateVersion: "Template Version",
  integrations: "Required for integration",
  useUUID: "Use UUID for Barcode values",
} as const;

export const CustomFieldDataTypes = [
  "String",
  "StringArray",
  "Integer",
  "IntegerArray",
  "Float",
  "FloatArray",
  "Boolean",
  "Date",
  "DateArray",
  "DateTime",
  "DateTimeArray",
  "Time",
  "TimeArray",
  "TimeRange",
  "DateTimeRange",
  "Dataset",
  "DatasetArray",
  "Sample",
  "SampleArray",
  "Project",
  "ProjectArray",
  "Person",
  "PersonArray",
  "Method",
  "MethodArray",
  "SharedContent",
  "SharedContentArray",
  "LabNotebook",
  "LabNotebookArray",
  "LabNotebookExperiment",
  "LabNotebookExperimentArray",
  "LabNotebookEntry",
  "LabNotebookEntryArray",
  "Attachment",
  "InventoryItem",
  "InventoryItemArray",
  "Barcode",
  "Url",
  "UrlArray",
] as const;

export type CustomFieldDataType = (typeof CustomFieldDataTypes)[number];

export interface CustomFieldSuggestions extends IEntityMinimalModel<CustomFieldBase["id"]> {}

export interface CustomFieldBaseRelations extends IRelationModel {}

export interface CustomFieldBaseFilters<SortingOptions>
  extends IGenericRequestParameters<CustomFieldBase, SortingOptions>,
    IPaginationParameters,
    INamedEntityParameters {
  dataTypes?: CustomFieldDataType[] | null;
}

export const CustomFieldBaseSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  "DATATYPE",
] as const;
export type CustomFieldBaseSortingOptions = (typeof CustomFieldBaseSortingOptionsConsts)[number];

export const CustomFieldBaseFiltersTranslator: PropertyTranslator<
  CustomFieldBaseFilters<CustomFieldBaseSortingOptions>
> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  ...INamedEntityParametersTranslator,
  dataTypes: "Data type(s)",
} as const;

export const CustomFieldDataTypeUtils = {
  CanDefinePredefinedOptionsFromValues: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "StringArray":
      case "Integer":
      case "IntegerArray":
      case "Float":
      case "FloatArray":
      case "Url":
      case "UrlArray":
        return true;
      default:
        return false;
    }
  },
  IsValueDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      // case "Barcode":
      //   return false;
      default:
        return true;
    }
  },
  IsDefaultValueDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      // case "Barcode":
      case "Attachment":
        return false;
      default:
        return true;
    }
  },
  IsValidableDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "Integer":
      case "Float":
      case "Url":
        return true;
      default:
        return false;
    }
  },
  IsStringDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "Date":
      case "DateTime":
      case "Time":
      case "Url":
        return true;
      default:
        return false;
    }
  },
  IsNumberDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "Integer":
      case "Float":
      case "IntegerArray":
      case "FloatArray":
        return true;
      default:
        return false;
    }
  },
  IsCustomizibleRegexDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "StringArray":
      case "Integer":
      case "IntegerArray":
      case "Float":
      case "FloatArray":
      case "Url":
      case "UrlArray":
        return true;
      default:
        return false;
    }
  },
  IsPrimitiveDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "StringArray":
      case "Integer":
      case "IntegerArray":
      case "Float":
      case "FloatArray":
      case "Boolean":
      case "Date":
      case "DateArray":
      case "DateTime":
      case "DateTimeArray":
      case "Time":
      case "TimeArray":
      case "DateTimeRange":
      case "Barcode":
      case "Url":
      case "UrlArray":
        return true;
      default:
        return false;
    }
  },
  IsSingleEntityReference: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "Dataset":
      case "Sample":
      case "Project":
      case "Person":
      case "Method":
      case "SharedContent":
      case "LabNotebook":
      case "LabNotebookExperiment":
      case "LabNotebookEntry":
      case "Attachment":
      case "InventoryItem":
        return true;
      default:
        return false;
    }
  },
  IsMultiEntityReference: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "DatasetArray":
      case "SampleArray":
      case "ProjectArray":
      case "PersonArray":
      case "MethodArray":
      case "SharedContentArray":
      case "LabNotebookArray":
      case "LabNotebookExperimentArray":
      case "LabNotebookEntryArray":
      case "InventoryItemArray":
        return true;
      default:
        return false;
    }
  },
  CanDefineCustomTypeConstraint: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "Dataset":
      case "Sample":
      case "Project":
      case "Person":
      case "Method":
      case "InventoryItem":
      case "DatasetArray":
      case "SampleArray":
      case "ProjectArray":
      case "PersonArray":
      case "MethodArray":
      case "InventoryItemArray":
        return true;
      default:
        return false;
    }
  },
  IsArrayDataType: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "StringArray":
      case "IntegerArray":
      case "FloatArray":
      case "DateArray":
      case "DateTimeArray":
      case "TimeArray":
      case "DateTimeRange":
      case "DatasetArray":
      case "SampleArray":
      case "ProjectArray":
      case "PersonArray":
      case "MethodArray":
      case "SharedContentArray":
      case "LabNotebookArray":
      case "LabNotebookExperimentArray":
      case "LabNotebookEntryArray":
      case "InventoryItemArray":
      case "UrlArray":
        return true;
      default:
        return false;
    }
  },
  HasEditForm: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      // case "Barcode":
      //   return false;
      default:
        return true;
    }
  },
  HasMultiSelect: (dataType: CustomFieldDataType) => {
    switch (dataType) {
      case "String":
      case "Integer":
      case "Float":
      case "Date":
      case "DateTime":
      case "Time":
      case "Dataset":
      case "Sample":
      case "Project":
      case "Person":
      case "Method":
      case "SharedContent":
      case "LabNotebook":
      case "LabNotebookExperiment":
      case "LabNotebookEntry":
      case "InventoryItem":
      case "StringArray":
      case "IntegerArray":
      case "FloatArray":
      case "DateArray":
      case "DateTimeArray":
      case "TimeArray":
      case "DatasetArray":
      case "SampleArray":
      case "ProjectArray":
      case "PersonArray":
      case "MethodArray":
      case "SharedContentArray":
      case "LabNotebookArray":
      case "LabNotebookExperimentArray":
      case "LabNotebookEntryArray":
      case "InventoryItemArray":
      case "Url":
      case "UrlArray":
        return true;
      default:
        return false;
    }
  },
  MapAbstractedEntityReferenceTypeToEntityConstants: (dataType?: EntitySelectOptions): EntityConstants => {
    switch (dataType) {
      case "Dataset":
        return datasetsConstants;
      case "Sample":
        return samplesConstants;
      case "Project":
        return projectsConstants;
      case "Person":
        return personsConstants;
      case "Method":
        return methodsConstants;
      case "SharedContent":
        return sharedContentConstants;
      case "LabNotebook":
        return labNoteBooksConstants;
      case "LabNotebookExperiment":
        return labNotebookExperimentsConstants;
      case "LabNotebookEntry":
        return labNotebookEntriesConstants;
      case "InventoryItem":
        return inventoriesConstants;
      default:
        throw new Error(`No entity constants found for data type: ${dataType}`);
    }
  },
  MapDataTypeToEntityConstants: (dataType?: CustomFieldDataType): EntityConstants => {
    switch (dataType) {
      case "Dataset":
      case "DatasetArray":
        return datasetsConstants;
      case "Sample":
      case "SampleArray":
        return samplesConstants;
      case "Project":
      case "ProjectArray":
        return projectsConstants;
      case "Person":
      case "PersonArray":
        return personsConstants;
      case "Method":
      case "MethodArray":
        return methodsConstants;
      case "SharedContent":
      case "SharedContentArray":
        return sharedContentConstants;
      case "LabNotebook":
      case "LabNotebookArray":
        return labNoteBooksConstants;
      case "LabNotebookExperiment":
      case "LabNotebookExperimentArray":
        return labNotebookExperimentsConstants;
      case "LabNotebookEntry":
      case "LabNotebookEntryArray":
        return labNotebookEntriesConstants;
      case "Attachment":
        return attachmentsConstants;
      case "InventoryItem":
      case "InventoryItemArray":
        return inventoryItemsConstants;
      default:
        throw new Error(`No entity constants found for data type: ${dataType}`);
    }
  },

  // CustomFieldValues utils
  IsValidOperatorForCustomFieldDataType: (
    operator: CustomFieldSearchOperator | null | undefined,
    dataType?: CustomFieldDataType
  ): boolean => {
    if (!operator) return false;
    if (!dataType) return false;
    return CustomFieldDataTypeUtils.MapCustomFieldValueOperatorToCustomFieldDataType(dataType).includes(operator);
  },

  MapCustomFieldValueOperatorToCustomFieldDataType: (
    dataType?: CustomFieldDataType
  ): readonly CustomFieldSearchOperator[] => {
    switch (dataType) {
      case "String":
      case "Url":
        return [
          "EQUALS",
          "NOT_EQUALS",
          "STRING_CONTAINS",
          "STRING_NOT_CONTAINS",
          "STARTS_WITH",
          "ENDS_WITH",
          "IS_NULL",
          "IS_NOT_NULL",
        ] as const;
      case "StringArray":
      case "UrlArray":
        return [
          "EQUALS",
          "NOT_EQUALS",
          "STRING_CONTAINS",
          "STRING_NOT_CONTAINS",
          "IN",
          "NOT_IN",
          "IS_NULL",
          "IS_NOT_NULL",
        ] as const;
      case "Integer":
      case "Float":
      case "Date":
      case "DateTime":
      case "Time":
      case "TimeRange":
      case "DateTimeRange":
        return [
          "EQUALS",
          "NOT_EQUALS",
          "GREATER_THAN",
          "GREATER_THAN_OR_EQUAL",
          "LESS_THAN",
          "LESS_THAN_OR_EQUAL",
          "IS_NULL",
          "IS_NOT_NULL",
        ] as const;
      case "IntegerArray":
      case "FloatArray":
      case "DateArray":
      case "DateTimeArray":
      case "TimeArray":
        return [
          "EQUALS",
          "NOT_EQUALS",
          "GREATER_THAN",
          "GREATER_THAN_OR_EQUAL",
          "LESS_THAN",
          "LESS_THAN_OR_EQUAL",
          "IN",
          "NOT_IN",
          "IS_NULL",
          "IS_NOT_NULL",
        ] as const;
      case "Boolean":
      case "Dataset":
      case "Sample":
      case "Project":
      case "Person":
      case "Method":
      case "SharedContent":
      case "LabNotebook":
      case "LabNotebookExperiment":
      case "LabNotebookEntry":
      case "Attachment":
      case "InventoryItem":
        return ["EQUALS", "NOT_EQUALS", "IS_NULL", "IS_NOT_NULL"] as const;
      case "DatasetArray":
      case "SampleArray":
      case "ProjectArray":
      case "PersonArray":
      case "MethodArray":
      case "SharedContentArray":
      case "LabNotebookArray":
      case "LabNotebookExperimentArray":
      case "LabNotebookEntryArray":
      case "InventoryItemArray":
        return ["EQUALS", "NOT_EQUALS", "IN", "NOT_IN", "IS_NULL", "IS_NOT_NULL"] as const;
      case "Barcode":
        return [
          "EQUALS",
          "NOT_EQUALS",
          "STRING_CONTAINS",
          "STRING_NOT_CONTAINS",
          "STARTS_WITH",
          "ENDS_WITH",
          "IS_NULL",
          "IS_NOT_NULL",
        ] as const;
      default:
        return [];
    }
  },
} as const;
